import { creerRuntime, type Runtime } from '@betool/identity-runtime'
import { adressePorte } from './porte'

/**
 * LE runtime d'identité du backoffice (`@betool/identity-runtime`, RFC §16),
 * créé UNE fois au démarrage et partagé par toute la page — miroir du
 * `identite.js` du kiosk (BBG-221), en TypeScript.
 *
 * ─── Ce que ce module ne touche pas ─────────────────────────────────────────
 *
 * Ni jeton, ni secret. Le runtime ne détient rien : le slotRef de CET onglet
 * vit dans SA clé sessionStorage (nom gelé par la porte RT-01 du SDK), et les
 * secrets des slots sont scellés dans le cookie HttpOnly du trousseau, posé par
 * le BFF d'identité — illisible ici, par construction (RFC §3, F5).
 *
 * ─── Le trousseau est MUTUALISÉ ─────────────────────────────────────────────
 *
 * Backoffice, « Mon compte » et kiosk parlent au MÊME BFF (identite/serveur) :
 * un seul conteneur scellé, jamais un second (doctrine « même trousseau »).
 * Ici le transport reste SAME-ORIGIN : en développement le relais Vite
 * (`/portail → 127.0.0.1:4200`, vite.config.ts) simule l'origine unique que la
 * production obtient en servant ce bundle depuis le BFF lui-même. La case
 * `origineBff` du SDK (transport cross-origine credentialed) est pour les
 * applications qui ne peuvent pas être servies par le BFF — pas pour celle-ci.
 *
 * ─── Pourquoi `lireJetonCsrf` vit ici ───────────────────────────────────────
 *
 * Le runtime ne lit JAMAIS un cookie lui-même (RT-03) : la lecture est une
 * injection obligatoire. Le cookie `betool-csrf` est volontairement NON
 * HttpOnly (identite/serveur/src/csrf.ts) : c'est le mécanisme du
 * double-submit, et sa valeur seule ne prouve rien — elle n'accompagne que le
 * scellé, que la page ne lit pas. Noms DÉFINIS ici, pas importés du BFF : les
 * deux sont des déploiements distincts qui ne partagent aucun module.
 */

const NOM_COOKIE_CSRF = 'betool-csrf'

/**
 * La lecture du double-submit, à ciel ouvert : l'adaptateur de comptes en a
 * besoin pour l'achat de la preuve (sa requête POST au BFF est gardée CSRF),
 * exactement comme le runtime pour ses propres mutations. Même cookie, même
 * règle — la valeur seule ne prouve rien.
 */
export function lireJetonCsrfPourPortail(): string | null {
  for (const paire of document.cookie.split(';')) {
    const [nom, ...reste] = paire.trim().split('=')
    if (nom === NOM_COOKIE_CSRF) {
      // `join('=')` : la valeur pourrait contenir le séparateur ; le découpage
      // naïf `split('=')[1]` la tronquerait en silence.
      const valeur = reste.join('=')
      return valeur === '' ? null : valeur
    }
  }
  return null
}

let runtime: Runtime | null = null


/**
 * Traduit la cible du SDK (« /connexion?ajout=1[&retour=…] », vocabulaire du
 * portail d'identité) vers la route de CETTE application : l'écran de
 * connexion du backoffice est `/login`, et son « retour après connexion » se
 * nomme `redirect`. La traduction vit à la frontière, pas dans le SDK — lui ne
 * connaît pas les routes de ses hôtes. Un `retour` qui ne serait pas un chemin
 * local est ÉCARTÉ : l'ouverture d'une cible arbitraire n'a pas de place ici.
 *
 * `ajout=1` est CONSERVÉ, pas absorbé : la garde du routeur replie `/login`
 * sur le portail quand une session est active — or l'ajout d'un compte se fait
 * PRÉCISÉMENT depuis une session active (comme « Ajouter un compte » chez
 * Google). Sans le marqueur, le clic reboucle sur le portail et l'ajout est
 * impossible.
 */
export function traduireCibleAjout(cible: string): string {
  if (!cible.startsWith('/connexion')) return cible
  /*
   * DIRECTEMENT À LA PORTE, et non par un `/login` d'ici.
   *
   * Cette application n'a plus d'écran de connexion : celui qui existait ne
   * faisait qu'appeler `allerALaPorte()` au montage, une page de transit qui
   * s'affichait le temps d'un battement et laissait croire que le backoffice a
   * sa propre connexion.
   *
   * `retour` n'a plus de destinataire : la porte compare l'ORIGINE aux points
   * d'entrée enregistrés, jamais un chemin. Le laissez-passer ramène ici de
   * toute façon.
   */
  return adressePorte({ ajout: true })
}

/**
 * L'ORIGINE DU DÉTENTEUR DU TROUSSEAU.
 *
 * Le trousseau est un cookie `__Host-` : il est attaché à l'origine EXACTE de
 * son gardien (`account.betool.fr`) et ne part jamais vers la nôtre. Sans cette
 * origine, le transport du runtime reste same-origin strict : il interroge
 * `backacces.betool.fr/portail/trousseau`, où le cookie n'arrive pas, et la
 * liste revient VIDE — sans erreur, sans rien dire.
 *
 * Mesuré en production par le banc croisé : « 0 vivant(s) » alors que le
 * trousseau en portait un. Aucune adoption de slot possible, donc aucune
 * surveillance possible, donc une déconnexion faite ailleurs qui n'arrivait
 * jamais jusqu'ici. Le commentaire qui annonçait « ici en same-origin » datait
 * de l'époque où ce SPA était servi sur l'origine de la porte ; il ne l'est
 * plus depuis la mise en production.
 *
 * Vide en développement (le relais Vite rend le same-origin vrai) : on ne pose
 * alors pas l'option, et le comportement d'avant est conservé tel quel.
 */
const ORIGINE_BFF = (import.meta.env.VITE_BETOOL_IDENTITE_BFF ?? '').replace(/\/+$/, '')

/**
 * Créé AVANT le mount (AppLayout) : synchrone, aucun réseau au démarrage —
 * il doit être prêt dès le premier geste sur le widget. Le canal inter-onglets
 * (BroadcastChannel) est celui par défaut du SDK : signaux seulement, jamais
 * de donnée (E4-T3). Relire après un F5 est l'affaire du premier listage.
 *
 * `naviguer` traduit au passage les cibles du SDK — `addAccount` vise
 * `/connexion?ajout=1`, qui n'existe pas dans le routeur du backoffice.
 */
export function ouvrirRuntimeIdentite(): Runtime {
  runtime = creerRuntime({
    lireJetonCsrf: lireJetonCsrfPourPortail,
    naviguer: (cible) => window.location.assign(traduireCibleAjout(cible)),
    // Voir ci-dessus : sans origine, le transport reste same-origin et le
    // trousseau est INVISIBLE depuis cette application.
    ...(ORIGINE_BFF === '' ? {} : { origineBff: ORIGINE_BFF }),
  })
  return runtime
}

/** Le runtime partagé — `null` tant que personne ne l'a ouvert. */
export function runtimeIdentite(): Runtime | null {
  return runtime
}
